import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Camera, ImagePlus, Download, Share2, Trash2, X, Loader2, Images } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";

// Galería de FOTOS REALES reutilizable (Admin inventario/consignadores + Portal consignador).
// scope="admin": base /admin/devices/{kind}/{deviceId}/photos · scope="portal": /portal/devices/{deviceId}/photos
export function DevicePhotos({ scope = "admin", kind = "new", deviceId, canUpload = false, count = 0, buttonClassName = "", label = "Fotos reales", compact = false }) {
  const base = scope === "portal" ? `/portal/devices/${deviceId}/photos` : `/admin/devices/${kind}/${deviceId}/photos`;
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [n, setN] = useState(count);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [thumbs, setThumbs] = useState({});
  const [viewer, setViewer] = useState(null); // {id, url, loading}
  const fileRef = useRef(null);
  const camRef = useRef(null);
  useEffect(() => setN(count), [count]);

  const fetchBlob = useCallback(async (pid, thumb) => {
    const r = await adminApi.get(`${base}/${pid}/file${thumb ? "?thumb=1" : ""}`, { responseType: "blob" });
    return URL.createObjectURL(r.data);
  }, [base]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(base);
      setPhotos(r.data.photos || []);
      setN(r.data.count || 0);
      setCanDelete(!!r.data.can_delete);
      const map = {};
      await Promise.all((r.data.photos || []).map(async (p) => { try { map[p.id] = await fetchBlob(p.id, true); } catch { /* skip */ } }));
      setThumbs(map);
    } catch { toast.error("No se pudieron cargar las fotos"); } finally { setLoading(false); }
  }, [base, fetchBlob]);

  useEffect(() => { if (open) load(); return () => { Object.values(thumbs).forEach((u) => URL.revokeObjectURL(u)); }; /* eslint-disable-next-line */ }, [open]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const r = await adminApi.post(base, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created?.length || 0} foto(s) agregada(s)`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir la foto"); } finally { setUploading(false); }
  };

  const openViewer = async (p) => {
    setViewer({ id: p.id, url: null, loading: true });
    try { const url = await fetchBlob(p.id, false); setViewer({ id: p.id, url, loading: false }); }
    catch { setViewer(null); toast.error("No se pudo abrir la imagen"); }
  };

  const download = async (p) => {
    try {
      const url = viewer?.id === p.id && viewer?.url ? viewer.url : await fetchBlob(p.id, false);
      const a = document.createElement("a"); a.href = url; a.download = `foto-${deviceId}-${p.id.slice(0, 6)}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("No se pudo descargar"); }
  };

  const share = async (p) => {
    try {
      const url = viewer?.id === p.id && viewer?.url ? viewer.url : await fetchBlob(p.id, false);
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `foto-${deviceId}.jpg`, { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Foto del equipo" });
      } else { toast.message("Compartir no disponible en este dispositivo; usa Descargar."); }
    } catch (e) { if (e?.name !== "AbortError") toast.error("No se pudo compartir"); }
  };

  const remove = async (p) => {
    if (!window.confirm("¿Eliminar esta fotografía? No afecta la venta ni los datos del equipo.")) return;
    try { await adminApi.delete(`${base}/${p.id}`); toast.success("Foto eliminada"); if (viewer?.id === p.id) setViewer(null); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <>
      <button type="button" title={`${label} (${n})`} data-testid={`device-photos-btn-${deviceId}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1.5 h-8 ${compact ? "px-2" : "px-2.5"} rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-xs font-medium transition-colors ${buttonClassName}`}>
        <Images className="w-3.5 h-3.5" /> {compact ? n : `${label} (${n})`}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`device-photos-dialog-${deviceId}`}>
          <DialogHeader>
            <DialogTitle>Fotos reales</DialogTitle>
            <DialogDescription>Fotografías reales del equipo <span className="font-mono">{deviceId}</span>. No sustituyen la imagen comercial.</DialogDescription>
          </DialogHeader>

          {canUpload && (
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid={`device-photos-input-${deviceId}`} />
              <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid={`device-photos-camera-${deviceId}`} />
              <button type="button" disabled={uploading || n >= 6} onClick={() => camRef.current?.click()} data-testid={`device-photos-take-${deviceId}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-xl bg-zinc-900 text-white text-sm font-medium disabled:opacity-40"><Camera className="w-4 h-4" /> Tomar foto</button>
              <button type="button" disabled={uploading || n >= 6} onClick={() => fileRef.current?.click()} data-testid={`device-photos-add-${deviceId}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-xl border border-zinc-300 text-zinc-700 text-sm font-medium disabled:opacity-40">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Agregar fotos</button>
            </div>
          )}
          {canUpload && <p className="text-[11px] text-zinc-400 -mt-1">Hasta 6 fotos · máx 5MB c/u. {n >= 6 && <span className="text-amber-600">Límite alcanzado.</span>}</p>}

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
          ) : photos.length === 0 ? (
            <p className="text-sm text-zinc-400 py-10 text-center" data-testid={`device-photos-empty-${deviceId}`}>Sin fotos reales todavía.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2" data-testid={`device-photos-grid-${deviceId}`}>
              {photos.map((p) => (
                <button key={p.id} type="button" onClick={() => openViewer(p)} data-testid={`device-photo-thumb-${p.id}`}
                  className="relative aspect-square rounded-xl overflow-hidden border border-zinc-200 bg-zinc-100">
                  {thumbs[p.id] ? <img src={thumbs[p.id]} alt="Foto real" className="w-full h-full object-cover" /> : <Loader2 className="w-4 h-4 animate-spin text-zinc-400 absolute inset-0 m-auto" />}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Visor ampliado */}
      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); } }}>
        <DialogContent className="rounded-3xl max-w-2xl p-0 overflow-hidden" data-testid="device-photo-viewer">
          <DialogHeader className="px-4 pt-4"><DialogTitle className="sr-only">Vista ampliada</DialogTitle><DialogDescription className="sr-only">Imagen ampliada</DialogDescription></DialogHeader>
          <div className="bg-black flex items-center justify-center min-h-[240px]">
            {viewer?.loading ? <Loader2 className="w-6 h-6 animate-spin text-white my-16" /> : viewer?.url ? <img src={viewer.url} alt="Foto real ampliada" className="max-h-[70vh] w-auto object-contain" /> : null}
          </div>
          <div className="flex flex-wrap gap-2 p-3">
            <button type="button" onClick={() => viewer && download({ id: viewer.id })} data-testid="device-photo-download" className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-xl border border-zinc-300 text-zinc-700 text-sm font-medium"><Download className="w-4 h-4" /> Descargar</button>
            <button type="button" onClick={() => viewer && share({ id: viewer.id })} data-testid="device-photo-share" className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-xl border border-zinc-300 text-zinc-700 text-sm font-medium"><Share2 className="w-4 h-4" /> Compartir</button>
            {canDelete && <button type="button" onClick={() => viewer && remove({ id: viewer.id })} data-testid="device-photo-delete" className="inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-xl bg-red-600 text-white text-sm font-medium"><Trash2 className="w-4 h-4" /></button>}
            <button type="button" onClick={() => { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); }} className="inline-flex items-center justify-center h-10 px-3 rounded-xl border border-zinc-300 text-zinc-700"><X className="w-4 h-4" /></button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
